import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'

import i18n from 'live/services/i18n'

import { IReduxActions, IReduxReducers } from '../../../../../../../../../../../store'
import { IBarrierTypeIcon, IEscalationFactor } from '../../../../../../../../../../../types'
import { Icon } from '../../../../../../../../../../../utils'
import {
    barrierWidth,
    escalationWidth,
    escalationHeight,
    barrierEditorGap,
    unfocusOpacity,
    barrierHeight
} from '../../../../../../../../../../../utils/constants'
import { BarrierEditor } from '../../BarrierEditor'
import { TextContainer } from './styles'

interface IProps {
    x: number
    y: number
    type: 'threat' | 'consequence'
    escalationData: IEscalationFactor
    escId: string
    xFather: number
    yFather: number
    rootXIndex: number
    unfocus: boolean
    barrierTypeIconMap: Map<number,IBarrierTypeIcon>
}

export const EscalationFactorEditor: React.FC<IProps> = ({
    x,
    y,
    type,
    escalationData,
    escId,
    xFather,
    yFather,
    rootXIndex,
    unfocus,
    barrierTypeIconMap
}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const { consequencesExpandedBarriers, threatsExpandedBarriers } = useSelector(
        (state: IReduxReducers) => state.bowtieExpandedBarriers
    )
    const [stateExpanded, setStateExpanded] = useState(false)

    useEffect(() => {
        const prevEscIdArr = escId.split('|')
        prevEscIdArr.pop()
        const prevEscId = prevEscIdArr.join('|')
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        if (expandedBarriers.length > 0) {
            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: expandedBarriers.map(e => {
                        if (e.barrierId === prevEscId) {
                            e.escalations = e.escalations.map(es => {
                                if (es.escId === escId) {
                                    es.y = y
                                }

                                return es
                            })
                        }

                        return e
                    })
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [y])

    const toggleBarriers = (): void => {
        const prevEscIdArr = escId.split('|')
        prevEscIdArr.pop()
        const prevEscId = prevEscIdArr.join('|')
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        if (expandedBarriers.length > 0) {
            if (stateExpanded) {
                dispatch<IReduxActions['bowtieExpandedBarriers']>({
                    type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                    payload: {
                        newExpandedBarriers: expandedBarriers.map(e => {
                            if (e.barrierId === prevEscId) {
                                e.escalations = e.escalations.map(es => {
                                    if (es.escId === escId) {
                                        es.x = x
                                    }

                                    return es
                                })
                            }

                            return e
                        })
                    }
                })
                dispatch<IReduxActions['bowtieExpandedBarriers']>({
                    type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                    payload: {
                        newExpandedBarriers: expandedBarriers.filter(e => !e.barrierId.startsWith(escId))
                    }
                })
                setStateExpanded(false)
            } else {
                dispatch<IReduxActions['bowtieExpandedBarriers']>({
                    type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                    payload: {
                        newExpandedBarriers: expandedBarriers.map(e => {
                            const newX =
                                type === 'threat'
                                    ? x - (escalationData?.barriers ?? []).length * (barrierWidth + barrierEditorGap)
                                    : x + (escalationData?.barriers ?? []).length * (barrierWidth + barrierEditorGap)
                            if (e.barrierId === prevEscId) {
                                e.escalations = e.escalations.map(es => {
                                    if (es.escId === escId) {
                                        es.x = newX
                                    }

                                    return es
                                })
                            }

                            return e
                        })
                    }
                })

                setStateExpanded(true)
            }
        }
    }

    return (
        <>
            <path
                d={`M ${xFather + barrierWidth / 2} ${yFather + barrierHeight} V ${y + escalationHeight / 2}  H ${
                    type === 'threat'
                        ? stateExpanded
                            ? x +
                              escalationWidth -
                              (escalationData.barriers ?? []).length * (barrierWidth + barrierEditorGap)
                            : x + escalationWidth
                        : stateExpanded
                        ? x -
                          escalationWidth +
                          (escalationData.barriers ?? []).length * (barrierWidth + barrierEditorGap)
                        : x - escalationWidth
                }`}
                className="bowtie-escalation-line"
                fill="none"
                strokeWidth={4}
                stroke={theme['gray-400']}
                opacity={focus === undefined || focus ? 1 : unfocusOpacity}
            />
            <g
                transform={`translate(${
                    stateExpanded
                        ? type === 'threat'
                            ? x - (escalationData.barriers || []).length * (barrierWidth + barrierEditorGap)
                            : x + (escalationData.barriers || []).length * (barrierWidth + barrierEditorGap)
                        : x
                }, ${y})`}
                id={escId}
                opacity={unfocus ? unfocusOpacity : 1}
            >
                <g transform={`${type === 'consequence' ? `rotate(180 0 0), translate(0 ${-escalationHeight})` : ''}`}>
                    <path
                        d="M124.307 1H5.06542C2.82015 1 1 2.82015 1 5.06542V64.9346C1 67.1798 2.82015 69 5.06542 69H124.249C125.911 69 127.405 67.9887 128.023 66.4462L140.377 35.6018C140.776 34.6057 140.765 33.4923 140.347 32.5043L128.05 3.47957C127.414 1.97645 125.94 1 124.307 1Z"
                        fill="#F9E47F"
                        stroke="#3866B1"
                        strokeWidth="0.813084"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </g>
                <foreignObject
                    x={type === 'consequence' ? -escalationWidth + 17 : 8}
                    y={8}
                    width={escalationWidth - 25}
                    height={escalationHeight - 16}
                    style={{ overflow: 'hidden' }}
                >
                    <TextContainer>{escalationData?.name ?? i18n('Escalation Factor')}</TextContainer>
                </foreignObject>
                <Icon
                    type="arrow"
                    x={
                        type === 'threat'
                            ? stateExpanded
                                ? escalationWidth + 10
                                : escalationWidth + 30
                            : stateExpanded
                            ? -(escalationWidth + 10)
                            : -(escalationWidth + 30)
                    }
                    y={escalationHeight / 2 - 8}
                    scale={type === 'threat' ? (stateExpanded ? '1' : '1 -1') : stateExpanded ? '1 -1' : '1'}
                    rotate={-90}
                    color={theme['gray-800']}
                    auxliarColor={theme['gray-200']}
                    clickEvent={toggleBarriers}
                />
            </g>
            {stateExpanded &&
                escalationData.barriers?.map((child, index, arr) => {
                    const barrierX =
                        type === 'threat'
                            ? x + barrierWidth / 1.25 - (barrierWidth + barrierEditorGap) * (arr.length - 1 - index)
                            : x - barrierWidth / 1.25 + (barrierWidth + barrierEditorGap) * (arr.length - 1 - index)

                    const barrierY = y + escalationHeight / 2 + 8
                    const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
                    const expanded = []
                    for (let i = 0; i < index; i++) {
                        expanded.push(
                            expandedBarriers
                                .filter(el => el.barrierId.startsWith(`${escId}|bar-${arr[i].id}`))
                                .map(d => (d.escalations ?? []).length)
                                .reduce((a, b) => a + b, 0)
                        )
                    }
                    const brothersLength = expanded.reduce((a, b) => a + b, 0)

                    return (
                        <BarrierEditor
                            key={`${escId}|bar-${child.id}`}
                            x={barrierX}
                            y={barrierY}
                            barrierData={child}
                            type={type}
                            escId={`${escId}|bar-${child.id}`}
                            rootXIndex={rootXIndex}
                            barrierBrothersY={brothersLength}
                            unfocus={unfocus}
                            barrierTypeIconMap={barrierTypeIconMap}
                        />
                    )
                })}
        </>
    )
}
